import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X, PlayCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_LABELS = {
    outdated_info: "Outdated content",
    broken_link: "Broken link",
    score_regression: "Score dropped",
    amc_due: "AMC renewal upcoming",
    amc_overdue: "AMC renewal overdue",
};

const SEVERITY_STYLES = {
    error: { bg: "#FEE2E2", fg: "#DC2626", ring: "border-[#DC2626]/25" },
    warning: { bg: "#FEF3C7", fg: "#B45309", ring: "border-[#F59E0B]/25" },
    info: { bg: "#DBE7FD", fg: "#3A80F7", ring: "border-[#3A80F7]/25" },
};

/**
 * Health Monitor panel for the dashboard.
 *
 * Shows open health alerts, a "Run checks now" button, and per-alert dismiss.
 */
export default function HealthMonitorPanel() {
    const [alerts, setAlerts] = useState([]);
    const [running, setRunning] = useState(false);

    async function load() {
        try {
            const r = await api.get("/me/health-alerts");
            setAlerts(r.data || []);
        } catch {
            // silent
        }
    }
    useEffect(() => { load(); }, []);

    async function runNow() {
        setRunning(true);
        try {
            await api.post("/me/health-alerts/run");
            await load();
            toast.success("Health checks complete");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Health check failed");
        } finally { setRunning(false); }
    }

    async function dismiss(id) {
        try {
            await api.post(`/me/health-alerts/${id}/dismiss`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to dismiss");
        }
    }

    return (
        <div className="ddi-card p-5" data-testid="health-monitor-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <ShieldAlert size={14} className="text-[#3A80F7]" />
                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                        Profile Health Monitor
                    </div>
                </div>
                <button
                    onClick={runNow}
                    disabled={running}
                    className="text-[11px] font-semibold text-[#3A80F7] hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid="health-monitor-run-btn"
                >
                    <PlayCircle size={12} /> {running ? "Running…" : "Run checks now"}
                </button>
            </div>
            {alerts.length === 0 ? (
                <div className="py-6 text-center text-[13px] text-[#64748B]" data-testid="health-monitor-empty">
                    All clear. No open alerts.
                </div>
            ) : (
                <ul className="space-y-2.5" data-testid="health-monitor-list">
                    {alerts.map((a) => {
                        const style = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
                        return (
                            <li
                                key={a.id}
                                className={"flex items-start gap-3 rounded-xl border p-3 " + style.ring}
                                style={{ background: style.bg + "40" }}
                                data-testid={`health-alert-${a.id}`}
                            >
                                <div
                                    className="mt-0.5 h-6 w-6 rounded-lg flex items-center justify-center shrink-0"
                                    style={{ background: style.bg, color: style.fg }}
                                >
                                    <AlertTriangle size={12} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="ddi-chip text-[10px]"
                                            style={{ background: style.bg, color: style.fg }}
                                        >
                                            {KIND_LABELS[a.kind] || a.kind}
                                        </span>
                                        {a.recurrence_count > 1 && (
                                            <span className="text-[10px] text-[#64748B]">seen {a.recurrence_count}×</span>
                                        )}
                                    </div>
                                    <div className="text-[13px] font-semibold text-[#0F172A] mt-1">{a.title}</div>
                                    <div className="text-[12px] text-[#64748B] mt-0.5">{a.body}</div>
                                    {(a.data || {}).url && (
                                        <a
                                            href={a.data.url}
                                            target="_blank"
                                            rel="noopener"
                                            className="text-[11px] text-[#3A80F7] hover:underline mt-1 inline-block font-mono truncate max-w-full"
                                        >
                                            {a.data.url}
                                        </a>
                                    )}
                                </div>
                                <button
                                    onClick={() => dismiss(a.id)}
                                    className="text-[#94A3B8] hover:text-[#DC2626] p-1"
                                    data-testid={`health-alert-dismiss-${a.id}`}
                                    title="Dismiss"
                                >
                                    <X size={13} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
